import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';

interface RowLock {
  table: string;
  rowId: string | number;
  lockedBy: string;
  timestamp: number;
}

interface RowLockContextType {
  lockedRows: RowLock[];
  lockRow: (table: string, rowId: string | number, user?: string) => boolean;
  unlockRow: (table: string, rowId: string | number) => void;
  isRowLocked: (table: string, rowId: string | number) => boolean;
  getLockOwner: (table: string, rowId: string | number) => string | null;
}

const RowLockContext = createContext<RowLockContextType | undefined>(undefined);

export const RowLockProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [lockedRows, setLockedRows] = useState<RowLock[]>([]);
  const { currentUser } = useAuth();

  const lockRow = (table: string, rowId: string | number, user?: string): boolean => {
    const lockingUser = user || currentUser;
    if (!lockingUser) return false;

    // Check if row is already locked by someone else
    const existingLock = lockedRows.find(
      lock => lock.table === table && lock.rowId === rowId
    );

    if (existingLock) {
      // If locked by current user, allow editing
      if (existingLock.lockedBy === lockingUser) {
        return true;
      }
      // If locked by another user, deny editing
      return false;
    }

    // Lock the row for current user
    const newLock: RowLock = {
      table,
      rowId,
      lockedBy: lockingUser,
      timestamp: Date.now(),
    };

    setLockedRows(prev => [...prev, newLock]);
    return true;
  };

  const unlockRow = (table: string, rowId: string | number) => {
    setLockedRows(prev => prev.filter(
      lock => !(lock.table === table && lock.rowId === rowId)
    ));
  };

  const isRowLocked = (table: string, rowId: string | number): boolean => {
    const lock = lockedRows.find(
      l => l.table === table && l.rowId === rowId
    );
    return !!lock;
  };

  const getLockOwner = (table: string, rowId: string | number): string | null => {
    const lock = lockedRows.find(
      l => l.table === table && l.rowId === rowId
    );
    return lock ? lock.lockedBy : null;
  };

  return (
    <RowLockContext.Provider value={{
      lockedRows,
      lockRow,
      unlockRow,
      isRowLocked,
      getLockOwner
    }}>
      {children}
    </RowLockContext.Provider>
  );
};

export const useRowLock = () => {
  const context = useContext(RowLockContext);
  if (context === undefined) {
    throw new Error('useRowLock must be used within a RowLockProvider');
  }
  return context;
};